/**
 * @Copyright 2014-2018 DataVoke, Inc. All rights reserved.
 */

import * as tools from '../utils/tools';
import { Record } from './recordApi';
import EventEmitter from '../support/eventemitter';
import * as Controls from './controlApi';

function createWidgetNameProperty (viewControls, widgetName) {
    if (viewControls[widgetName] == null) {
        // if we don't have an array for this widget on the object, create one.
        viewControls[widgetName] = [];
    }

    if (!Array.isArray(viewControls[widgetName])) {
        // if we have a non-Array property name that is the same as a widget name, then the widget was named oddly; put up a warning.
        console.warn(`Discovered widget named after object property: "${widgetName}"; attempting to use "widget_${widgetName}"`);
        return createWidgetNameProperty(viewControls, `widget_${widgetName}`);
    }

    return widgetName;
}

/**
 * Takes the given raw control and returns the API wrapper for the control
 * @param {*} control - The raw control to wrap
 * @param {Object} [runtime] - The optional runtime object for this control.
 * @returns {BaseControl} - The control API wrapper
 * @private
 */
function wrapControl (control, runtime) {
    if (control != null) {
        if (control.metadata) {
            switch (control.metadata.typeName) {
                case "dvLayoutItem.field":
                    return new Controls.FieldControl(control, runtime);
                default:
                    return new Controls.BaseControl(control, runtime);
            }
        }
        else {
            return new Controls.BaseControl(control, runtime);
        }
    }
}

class ViewControls {
    /**
     * This object contains arrays of form controls for a view API object.
     * @param {LiteFormController|null} [fc = null] - (OPTIONAL) Form controller to populate this object.
     */
    constructor (fc = null) {
        this.allFields = [];
        this.allButtons = [];
        this.allEmbeddedForms = [];
        this.allLabels = [];
        this.allGroupPanels = [];
        this.allTabControls = [];
        this.allCharts = [];
        this.grid = null;

        if (fc) {
            this.refresh(fc);
        }
    }

    /**
     * Clear all object control arrays.
     */
    clear () {
        for (let n in this) {
            if (this.hasOwnProperty(n)) {
                if (Array.isArray(this[n])) {
                    this[n].forEach((controlApi) => {
                        controlApi.dispose();
                    });
                    this[n].length = 0;
                }
                else {
                    this[n] = null;
                }
            }
        }
    }

    /**
     * Clear all object control arrays and rebuild them from the specified form controller.
     * @param {LiteFormController} fc - The form controller to build the control arrays from.
     */
    refresh (fc) {
        let runTimeMap = new Map();
        for (let viewControlArray in this) {
            if (this.hasOwnProperty(viewControlArray) && Array.isArray(this[viewControlArray])) {
                for (let viewControl of this[viewControlArray]) {
                    if (viewControl && viewControl.raw && viewControl.runtime) {
                        runTimeMap.set(viewControl.raw, viewControl.runtime);
                    }
                }
            }
        }
        if (this.grid) {
            runTimeMap.set(this.grid.raw, this.grid.runtime);
        }
        if (this.cardList) {
            runTimeMap.set(this.cardList.raw, this.cardList.runtime);
        }

        this.clear();

        if (fc == null || fc.mvc == null || fc.mvc.isDisposed || fc.mvc.isClosing) {
            // if something happened due to the form controller or mvc closing, we don't want to attempt to reload...
            return;
        }

        // load all form widgets from the form controller. NOTE: these will create new arrays on this object using the name of each widget.
        fc.formWidgets.Values.forEach((uig) => {
            let widgetUID = uig.definitionID,
                widget = app.dv.cache.extensions.registeredWidgets.getVal({UID: widgetUID});

            if (widget == null) {
                // If we didn't find the widget, then we simply can't work on it...How did it get there?
                console.warn(`Unregistered widget discovered on form ${fc.mvc.drivingView.Definition.Name}; widget UID: ${widgetUID}`);
                return;
            }

            let useWidgetName = widget.Name;

            if (widget.Name === 'CardList' && widget.UID === app.dv.widgets.widgetStores.core.CardList.UID) {
                // there should be only one...
                this._addControl(uig.uiControl[0], undefined, 'cardList', runTimeMap.get(uig.uiControl[0]));
                return;
            }
            else if (widget.Name === 'ImagePanel' && widget.UID === app.dv.widgets.widgetStores.core.ImagePanel.UID) {
                useWidgetName = 'allImagePanels';
            }

            // validate and create the property based on the widget name.
            let widgetName = createWidgetNameProperty(this, useWidgetName);

            // add all the controls using this widget from the form controller to the array created under the validated widget name
            uig.uiControl.forEach((control) => {
                this._addControl(control, this[widgetName], undefined, runTimeMap.get(control));
            });
        });

        // load all form values from the form controller.
        fc.formValues.Values.forEach((uig) => {
            uig.uiControl.forEach((control) => {
                this._addControl(control, this.allFields, undefined, runTimeMap.get(control));
            });
        });

        // load all form action buttons from the form controller.
        fc.formActions.Values.forEach((uig) => {
            uig.uiControl.forEach((control) => {
                this._addControl(control, this.allButtons, undefined, runTimeMap.get(control));
            });
        });

        // load all form views and grid from the form controller.
        fc.formViews.Values.forEach((uig) => {
            uig.uiControl.forEach((control) => {
                if (uig.definitionID === fc.mvc.drivingView.ViewID) {
                    // there should be only one...
                    this._addControl(control, undefined, 'grid', runTimeMap.get(control));
                }
                else {
                    this._addControl(control, this.allEmbeddedForms, undefined, runTimeMap.get(control));
                }
            });
        });

        // load all form labels from the form controller.
        fc.formLabels.Values.forEach((uig) => {
            uig.uiControl.forEach((control) => {
                this._addControl(control, this.allLabels, undefined, runTimeMap.get(control));
            });
        });

        // load all form groups from the form controller.
        fc.formGroups.forEach((group) => {
            this._addControl(group, this.allGroupPanels, undefined, runTimeMap.get(group));
        });

        // load all form tabs from the form controller.
        fc.formTabs.forEach((tab) => {
            this._addControl(tab, this.allTabControls, undefined, runTimeMap.get(tab));
        });

        // load all form charts from the form controller.
        fc.formCharts.forEach((uig) => {
            uig.uiControl.forEach((control) => {
                this._addControl(control, this.allCharts, undefined, runTimeMap.get(control));
            });
        });
    }

    /**
     * Clear out and dispose of control references.
     */
    dispose () {
        for (let n in this) {
            if (this.hasOwnProperty(n)) {
                if (Array.isArray(this[n])) {
                    this[n].forEach((controlApi) => {
                        controlApi.dispose();
                    });
                    this[n].length = 0;
                }
                this[n] = null;
            }
        }
    }

    /**
     * Adds the given control to the ViewControl's object.
     * The control will be added directly by name if it exists, into the array if specified, and/or directly by propertyName if specified.
     * @param {Object} control - The control to add
     * @param {Array} [array] - Optional array to add the control to
     * @param {string} [propertyName] Optional string to use as the property for a direct reference
     * @param {Object} [runtime] - The optional runtime object for this control.
     * @private
     */
    _addControl (control, array, propertyName, runtime) {
        let wrappedControl = wrapControl(control, runtime);

        // Add to the array if specified
        if (array && array.findIndex(controlWrapper => controlWrapper.raw === control) < 0) {
            array.push(wrappedControl);
        }

        // Add directly by propertyName if specified
        if (propertyName) {
            this[propertyName] = wrappedControl;
        }

        // Add directly by control name if it exists
        if (control.definition && control.definition.ID) {
            if (this[control.definition.ID] == null) {
                this[control.definition.ID] = wrappedControl;
            }
            else {
                console.warn(`There already exists a controls API referenced for ${control.definition.ID}. `)
            }
        }
    }
}

/**
 * The API object to represent MVC objects.
 * @class
 * @classdesc API View Object
 */
export class View extends EventEmitter {
    /**
     * Wrap the specified MVC object as a new API View object. Optional specified parent API View object allows for hierarchy.
     * @param {object} _mvc - The source MVC object this API View object wraps.
     * @param {View} [parent = null] - OPTIONAL The parent API View object, if this is not a top-level API View object.
     */
    constructor(_mvc, parent = null) {
        super();

        let self = this;

        /**
         * Internal reference to the MVC object.
         * @type {Object}
         */
        this.raw = _mvc;

        /**
         * Object that should be used for custom logic to prevent breaking the core API
         * @type {Object}
         */
        this.runtime = {};

        /**
         * Track the api Reference on the raw object so we can translate back and forth easily...EK
         * @type {View}
         */
        _mvc.apiRef = self;

        /**
         *
         * @type {View|null}
         */
        this.parent = parent;
        if (parent && !_mvc.isDropDown && !_mvc.isAnalysisDetail) {
            // add ourselves to the parent's kids, if the parent is specified.
            parent.kids.push(self);
        }

        /**
         * Reference to all child API View objects (must be stored here to be instance specific)...EK
         * @type {Array}
         * @private
         */
        this._kids = [];

        // go through the child MVC objects of the source MVC to create new API View objects for each.
        this.raw.children.forEach((v) => {
            if (v.isDropDown || v.isAnalysisDetail) {
                return;
            }
            // NOTE: doing this will add the sub-view to this view, since we are passing in ourselves as the parent.
            let subView = new View(v, self);
        });

        // Add a handler for when the MVC disposes.
        _mvc.on("disposed", (mvcInstance) => {
            tools.resetThisScope(self, self.dispose);
        });

        /**
         * Flag to indicate if this object has been disposed.
         * @type {boolean}
         */
        this.isDisposed = false;

        /**
         * Internal dictionary of API Record objects to sync up with MVC GridRecord objects.
         * @type {Dictionary}
         * @private
         */
        this._records = new tools.Dictionary();

        //Sync the current record list into the API reference list...EK
        this.raw.records.Values.forEach((gr) => {
            self._records.add(gr.clientRecord.Record.RecordID, new Record(self, gr));
        });

        //#region Dictionary listeners for syncing API Record with MVC GridRecords

        //Add a listener for added/removed/cleared records to keep our API dictionary in sync...EK
        this.raw.records.on('add', (addedRecord) => {
            // add a new API Record object to wrap the GridRecord object that was added to the source dictionary.
            self._records.add(addedRecord.value.clientRecord.Record.RecordID, new Record(self, addedRecord.value));
        });
        this.raw.records.on('insert', (insertedRecord, uid, atIndex) => {
            // insert a new API Value object to wrap the GridValue object that was inserted into the source dictionary.
            self._records.insert(insertedRecord.key, new Record(self, insertedRecord.value), uid, atIndex);
        });
        this.raw.records.on('remove', (removedRecord) => {
            // remove the API Record object so that we remain synced with the source dictionary.
            self._records.remove(removedRecord.key);
        });
        this.raw.records.on('swap', (firstItem, firstIndex, secondItem, secondIndex) => {
            // swap the API Record objects at the specified indices so that the dictionaries remain synced.
            self._records.swap(firstIndex, secondIndex);
        });
        this.raw.records.on('append', (appendDictionary) => {
            // create a temp dictionary so we use it to create API Records objects for each GridRecord object.
            let tmp = new tools.Dictionary();

            // make reference copies of the keys and uid arrays.
            tmp.Keys = appendDictionary.Keys.slice();
            tmp.KeysUID = appendDictionary.KeysUID.slice();

            // for each GridRecord in the appended dictionary, create a new API Record in our temp dictionary.
            appendDictionary.Values.forEach((item) => {
                tmp.Values.push(new Record(self, item));
            });

            // append the new dictionary so we remain synced with the source dictionary.
            self._records.append(tmp);
        });
        this.raw.records.on('updateKey', (originalKey, newKey) => {
            // we need to update the key values for our API object dictionary.
            self._records.updateKey(originalKey, newKey);
        });
        this.raw.records.on('clear', () => {
            self._records.clear();
        });

        //#endregion

        /**
         * Get a reference for all the controls on the MVC's form controller (may not be created yet).
         * @type {ViewControls}
         */
        this.controls = new ViewControls(_mvc.formController);
    }

    /**
     *
     * @param {function} callback
     */
    addRecord(callback = tools.noop) {
        let self = this;

        return new Promise((resolve, reject) => {
            let fn = app.dv.actionLibrary.actionStores.dvCore.NewRecord.Definition.Execute,
                adHocResult = new app.dv.actionLibrary.result(true, true);

            if (fn == null) {
                adHocResult.actionMessage += "\nCannot create new record.";
                app.dv.actionLibrary.executeShowMessage(contextMvc, null, source, adHocResult.actionMessage, true, null, false, null);
                // cannot save, stop execution.
                reject(adHocResult);
                callback(adHocResult);
            } else {
                let exeOptions = {
                    action: app.dv.actionLibrary.actionStores.dvCore.NewRecord.Definition,
                    context: {
                        view: self
                    },
                    defaultSettings: {},
                    source: null
                };
                fn(exeOptions, function (adHocResult2) {
                    if (!adHocResult2.success ||
                        !adHocResult2.continueExecution) {
                        if (adHocResult2.actionMessage === "") {
                            adHocResult2.actionMessage = "Cannot create new record.";
                        } else {
                            adHocResult2.actionMessage = "Cannot create new record.\n\n" + "REASON: " + adHocResult2.actionMessage;
                        }
                        // there was an error; let the user know and stop the action chain.
                        adHocResult2.success = adHocResult2.continueExecution = false;
                        reject(adHocResult2);
                        callback(adHocResult2);
                    } else {
                        adHocResult2.success = adHocResult2.continueExecution = true;
                        resolve(adHocResult2);
                        callback(adHocResult2);
                    }

                });
            }
            // app.dv.actions.newRecord.adHoc(this.raw, callback);
        });
    }

    /**
     * Execute the specified action ID for this View
     * @param {number} actionID
     * @param {function} callback
     */
    executeActionChain(actionID, callback = tools.noop) {
        return new Promise((resolve, reject) => {
            app.dv.mvc.executeMacro(this.raw, actionID, (result) => {
                resolve(result);
                callback(result);
            });
        });
    }

    /**
     * Contains an array (NOT dictionary) of children views embedded inside this view.
     *  Note: this is a static list, if designer changes occur a reload of the view would be needed to see changes here (limit of current view tracking as array).
     */
    get kids() {
        return this._kids;
    }

    /**
     * @returns {String} Returns the internal name of this view.
     */
    get name() {
        return this.raw.drivingView.Name;
    }

    /**
     * @returns {Object} Returns the currently selected record in the view (if more than one, it returns the focused selected record).
     */
    get selectedRecord() {
        if (this.raw.selectedRecord != null && !this.raw.selectedRecord.isDisposed && this.raw.selectedRecord.clientRecord != null) {
            return this.records.getVal(this.raw.selectedRecord.clientRecord.Record.RecordID);       // will return the selected api record reference...EK
        }
        else {
            return null;
        }
    }

    /**
     * Sets the currently selected record in the view (this will clear any other selection).
     * @params {Record|GridRecord}
     */
    set selectedRecord(selectedRecord) {
        // If the api record or grid record is specified, be sure only the grid record makes it to the actual internal call...EK
        if (selectedRecord.raw != null) {
            selectedRecord = selectedRecord.raw;
        }
        app.dv.mvc.setSelectedRecord(this.raw, selectedRecord);
    }

    /**
     * Gets the currently selected records in the view.
     */
    get selectedRecords() {
        let selectedRecords = this.raw.gridController.getSortedRecords(true);
        // We need to be sure that we return a list of api record references (search where it's found and matches in our list)...EK
        let list = [];
        // this is faster than a filter as we only have to go through the records that are actually selected.
        selectedRecords.forEach((record) => {
            list.push(record.apiRef);
        });
        return list;
    }

    /**
     * Returns the title of this view.
     * @returns {String}
     */
    get title() {
        return this.raw.drivingView.Definition.Name;
    }

    /**
     * Returns the ID of this view.
     * @returns {Integer}
     */
    get viewID() {
        return this.raw.drivingView.ViewID;
    }

    /**
     * Used to remove the selected record(s) from the view.  These records will NOT be deleted.
     * @param {Function} callback: (optional) A function that should be called when the action is complete.
     */
    removeRecord(callback = tools.noop) {
        let self = this;

        return new Promise((resolve, reject) => {
            let fn = app.dv.actionLibrary.actionStores.dvCore.RemoveRecord.Definition.Execute,
                adHocResult = new app.dv.actionLibrary.result(true, true);
            if (fn == null) {
                adHocResult.actionMessage += "\nCannot remove record.";
                app.dv.actionLibrary.executeShowMessage(contextMvc, null, source, adHocResult.actionMessage, true, null, false, null);
                // cannot save, stop execution.
                reject(adHocResult);
                callback(adHocResult);
            } else {
                let exeOptions = {
                    action: app.dv.actionLibrary.actionStores.dvCore.RemoveRecord.Definition,
                    context: {
                        view: self
                    },
                    defaultSettings: {},
                    source: null
                };
                fn(exeOptions, function (adHocResult2) {
                    if (!adHocResult2.success ||
                        !adHocResult2.continueExecution) {
                        if (adHocResult2.actionMessage === "") {
                            adHocResult2.actionMessage = "Cannot remove record.";
                        } else {
                            adHocResult2.actionMessage = "Cannot remove record.\n\n" + "REASON: " + adHocResult2.actionMessage;
                        }
                        // there was an error; let the user know and stop the action chain.
                        adHocResult2.success = adHocResult2.continueExecution = false;
                        reject(adHocResult2);
                        callback(adHocResult2);
                    } else {
                        adHocResult2.success = adHocResult2.continueExecution = true;
                        resolve(adHocResult2);
                        callback(adHocResult2);
                    }

                });
            }
        });
    }

    /**
     * Used to determine if a save is required for the current view (i.e. data has changed).
     * @returns {boolean}
     */
    saveIsRequired() {
        return app.dv.mvc.isDirty(this.raw);
    }

    /**
     * Selects the first record in the view (as displayed).
     */
    selectFirst() {
        if (this.raw.gridController != null) {
            this.raw.gridController.selectFirst();
        }
    }

    /**
     * Selects the last record in the view (as displayed).
     */
    selectLast() {
        if (this.raw.gridController != null) {
            this.raw.gridController.selectLast();
        }
    }

    /**
     * Selects the next record in the view (as displayed).
     */
    selectNext() {
        if (this.raw.gridController != null) {
            this.raw.gridController.selectNext();
        }
    }

    /**
     * Selects the previous record in the view (as displayed).
     */
    selectPrev() {
        if (this.raw.gridController != null) {
            this.raw.gridController.selectPrev();
        }
    }

    /**
     * Creates an expression object and evaluates the expression.
     * @param {string} expression - The string expression to be evaluated.
     * @param {Record|GridRecord} record - The record object to use to evaluate the expression.
     * @param {object} action - An object that is an action, or object to simulate action for unique ID properties.
     * @param {number} action.actionChainID - Used to create a unique ID for this expression within the source MVC.
     * @param {number} action.stepID - Used to create a unique ID for this expression within the source MVC.
     * @returns {*}
     */
    evaluateExpression(expression, record, action) {
        if (record.raw != null) {
            record = record.raw;
        }
        // Create the expression object used to evaluate the expression..PW
        let viewID = tools.getOrDefault(this, 'raw.drivingView.ViewID', 0),
            expr = app.dv.mvc.createExpression(this.raw, action != null ? action.actionChainID : null, action != null ? action.stepID : null, 0, null, viewID, expression, null);

        if (expr) {
            try {
                // Evaluate the expression and return the result..PW
                return expr.evaluate(viewID, record);
            }
            catch (e) {
                if (app.designer) {
                    app.browser.notifier.showToast(`Error evaluating API expression:<br/><br/>${expression}<br/><br/>Error: ${e.message}`);
                }
            }
        }
        return null;
    }

    /**
     * Exposes a dictionary of records that are in the view (may not match the display order).
     * @returns {Dictionary}
     */
    get records() {
        return this._records;
    }

    /**
     * Used to do a full refresh of the data on the current view using the current view-level filters.
     * @param {Function} [callback = tools.noop] - (optional) A function that should be called when the refresh is complete.
     */
    refreshData(callback = tools.noop) {
        return new Promise((resolve, reject) => {
            app.dv.mvc.refreshMvcInstance(this.raw, (result) => {
                resolve(result);
                callback(result);
            });
        })
    }

    /**
     * Used to save the data on the current view that has not been committed yet.
     * @param {Function} [callback = tools.noop] - (optional) A function that should be called when the refresh is complete.
     */
    saveData(callback = tools.noop) {
        let self = this;

        return new Promise((resolve, reject) => {
            let fn = app.dv.actionLibrary.actionStores.dvCore.Save.Definition.Execute,
                adHocResult = new app.dv.actionLibrary.result(true, true);
            if (fn == null) {
                adHocResult.actionMessage += "\nCannot save.";
                app.dv.actionLibrary.executeShowMessage(contextMvc, null, source, adHocResult.actionMessage, true, null, false, null);
                // cannot save, stop execution.
                reject(adHocResult);
                callback(adHocResult);
            } else {
                let exeOptions = {
                    action: app.dv.actionLibrary.actionStores.dvCore.Save.Definition,
                    context: {
                        view: self
                    },
                    defaultSettings: {
                        SkipNewRecords: null,
                        SuppressSpinner: null,
                        saveForRefresh: null
                    },
                    source: null
                };
                fn(exeOptions, function (adHocResult2) {
                    if (!adHocResult2.success ||
                        !adHocResult2.continueExecution) {
                        if (adHocResult2.actionMessage === "") {
                            adHocResult2.actionMessage = "Cannot save.";
                        } else {
                            adHocResult2.actionMessage = "Cannot save.\n\n" + "REASON: " + adHocResult2.actionMessage;
                        }
                        // there was an error; let the user know and stop the action chain.
                        adHocResult2.success = adHocResult2.continueExecution = false;
                        reject(adHocResult2);
                        callback(adHocResult2);
                    } else {
                        adHocResult2.success = adHocResult2.continueExecution = true;
                        resolve(adHocResult2);
                        callback(adHocResult2);
                    }

                });
            }
            // app.dv.actions.saveAction.adHoc(this.raw, callback);
        });
    }

    /**
     * Gets the layout item at the root form level that matches the specified ID string
     * @param {String} ID - The ID/Name of the layout item to search for
     * @returns {Object} The matched control if found, null otherwise
     */
    findControl(ID) {
        let control = this.raw.findControl(ID);
        // Make sure the control is wrapped and not just a raw reference to the control
        return control && (control.apiRef || wrapControl(control));
    }

    /**
     * Update API control references based on current form controller control dictionaries.
     */
    updateControls () {
        this.controls.refresh(this.raw.formController);

        // ensure all sub-views are updated as well.
        this._kids.forEach((subView) => {
            subView.updateControls();
        })
    }

    /**
     * Internal Use.  Used to assist memory clean-up when a view is closed.
     */
    async dispose() {
        if (this.isDisposed) {
            return;
        }
        this.controls.dispose();
        this.controls = null;
        if (this.parent != null) {
            this.parent.kids.remove(this);
        }
        this.raw.apiRef = null;
        this.raw = null;
        this.parent = null;
        let disposedRecords = [];
        this._records.Values.forEach((rec) => {
            disposedRecords.push(rec.dispose());
        });
        await Promise.all(disposedRecords);
        this._records.clear();
        this._records = null;
        this._kids.length = 0;
        this.isDisposed = true;
    }
}
